import { t } from "../i18n";
import type { OrthoStack, StackLayer } from "../plan/OrthoStack";
import type { PlanTool, PlanView } from "../plan/PlanView";
import { DPI_PRESETS, SCALE_PRESETS, type Orientation, type PaperSize, type PrintUiState } from "../plan/pdfExport";
import { formatBytes } from "../util/format";
import { el, section, selectBox, slider } from "./dom";

/**
 * "Orthophotos" sidebar section for the plan mode: every loaded TIFF, in paint order (top of
 * the list = top of the stack = wins where two overlap), with per-layer visibility, "bring to
 * front" and remove controls. `onChange` re-renders this section and re-composites the canvas
 * — see `App`'s plan-mode wiring.
 */
export function buildOrthoSection(stack: OrthoStack, onChange: () => void, onAddFiles: (files: File[]) => void): HTMLElement {
  const handle = section(t().orthophotos, { collapsed: false });
  handle.setBadge(stack.all.length ? String(stack.all.length) : null);

  if (stack.isEmpty) {
    handle.body.append(el("div", { class: "hint", text: t().noOrthophotosLoaded }));
  } else {
    // Topmost layer (last in paint order) shown first, matching what's visually on top.
    for (const layer of [...stack.all].reverse()) {
      handle.body.append(layerRow(stack, layer, onChange));
    }
  }

  // Drag-and-drop works too (see App.acceptDroppedFiles), but a file input is the only way
  // in when there's no Trimble Connect project browser around — standalone dev mode, or a
  // deployment reached outside of Connect.
  const input = el("input", { type: "file", accept: ".tif,.tiff", multiple: true, hidden: true });
  input.addEventListener("change", () => {
    if (input.files?.length) onAddFiles([...input.files]);
    input.value = "";
  });
  const addButton = el("button", { type: "button" }, t().addTiffButton);
  addButton.addEventListener("click", () => input.click());
  handle.body.append(el("div", { class: "row" }, addButton, input));
  handle.body.append(el("div", { class: "hint", text: t().addOrthophotoHint }));
  return handle.root;
}

function layerRow(stack: OrthoStack, layer: StackLayer, onChange: () => void): HTMLElement {
  const visibleButton = el(
    "button",
    { class: "icon", type: "button", title: layer.visible ? t().layerVisible : t().layerHidden },
    layer.visible ? "👁" : "🚫",
  );
  visibleButton.addEventListener("click", () => {
    stack.setVisible(layer.id, !layer.visible);
    onChange();
  });

  const frontButton = el("button", { class: "icon", type: "button", title: t().bringToFront }, "⤒");
  frontButton.addEventListener("click", () => {
    stack.bringToFront(layer.id);
    onChange();
  });

  const removeButton = el("button", { class: "icon", type: "button", title: t().removeLayer }, "✕");
  removeButton.addEventListener("click", () => {
    stack.remove(layer.id);
    onChange();
  });

  const overlaps = layer.visible && stack.overlapsAnother(layer.id);
  return el(
    "div",
    { class: "row plan-layer-row" },
    el("span", { class: "grow", text: `${layer.source.label} · ${formatBytes(layer.source.bytes)}` }),
    ...(overlaps ? [el("span", { class: "badge warn", text: t().overlapBadge })] : []),
    visibleButton,
    frontButton,
    removeButton,
  );
}

const PAPER_SIZES: PaperSize[] = ["A4", "A3", "A2", "A1", "A0"];

/**
 * "Print & export" section: paper/orientation/scale/DPI controls plus the export button.
 * `state` is mutated in place (a plain object owned by `App`) rather than round-tripped
 * through re-renders — the controls only ever need to read/write it, never react to it
 * changing from elsewhere.
 */
export function buildPrintExportSection(stack: OrthoStack, state: PrintUiState, onExport: () => void): HTMLElement {
  const handle = section(t().printExport, { collapsed: false });

  if (stack.isEmpty) {
    handle.body.append(el("div", { class: "hint", text: t().exportNeedsLayer }));
    return handle.root;
  }

  handle.body.append(
    selectBox<PaperSize>(
      t().paperSize,
      PAPER_SIZES.map((p) => ({ value: p, label: p })),
      state.paper,
      (value) => (state.paper = value),
    ),
    selectBox<Orientation>(
      t().orientationLabel,
      [
        { value: "portrait", label: t().orientationPortrait },
        { value: "landscape", label: t().orientationLandscape },
      ],
      state.orientation,
      (value) => (state.orientation = value),
    ),
  );

  const scaleInput = el("input", { type: "number", min: "1", step: "1", value: String(state.scaleDenominator), list: "plan-scale-presets" });
  scaleInput.addEventListener("change", () => {
    const value = Number(scaleInput.value);
    if (Number.isFinite(value) && value > 0) state.scaleDenominator = value;
  });
  const datalist = el("datalist", { id: "plan-scale-presets" });
  for (const preset of SCALE_PRESETS) datalist.append(el("option", { value: String(preset) }));
  handle.body.append(
    el("div", { class: "field" }, el("span", { text: t().scaleLabel }), el("div", { class: "row" }, "1 :", scaleInput, datalist)),
  );

  handle.body.append(
    selectBox<string>(
      t().dpiLabel,
      DPI_PRESETS.map((dpi) => ({ value: String(dpi), label: `${dpi} dpi` })),
      String(state.dpi),
      (value) => (state.dpi = Number(value)),
    ),
  );

  const exportButton = el("button", { class: "primary", type: "button" }, t().exportPdf);
  exportButton.addEventListener("click", onExport);
  handle.body.append(exportButton);

  return handle.root;
}

const PEN_COLORS = ["#e63946", "#1d3557", "#2a9d8f", "#f4a261", "#111111", "#ffffff"];
const TOOLS: Array<{ tool: PlanTool; label: () => string }> = [
  { tool: "pan", label: () => t().toolPan },
  { tool: "pen", label: () => t().toolPen },
  { tool: "text", label: () => t().toolText },
];

/** "Annotate" section: pan/pen/text tool switch, colour, pen width, undo/clear. Purely a thin
 *  front-end onto `PlanView`'s own tool/annotation state — see `plan/PlanView.ts`. */
export function buildAnnotateSection(view: PlanView): HTMLElement {
  const handle = section(t().annotate, { collapsed: false });

  const toolRow = el("div", { class: "row plan-tools" });
  const toolButtons: Array<[PlanTool, HTMLButtonElement]> = [];
  const syncTools = () => {
    for (const [tool, button] of toolButtons) button.dataset.active = String(view.tool === tool);
  };
  for (const { tool, label } of TOOLS) {
    const button = el("button", { type: "button" }, label());
    button.addEventListener("click", () => {
      view.setTool(tool);
      syncTools();
    });
    toolButtons.push([tool, button]);
    toolRow.append(button);
  }
  syncTools();
  handle.body.append(toolRow);

  const swatches = el("div", { class: "plan-color-swatches" });
  const syncSwatches = () => {
    for (const swatch of [...swatches.children]) (swatch as HTMLElement).dataset.active = String((swatch as HTMLElement).dataset.color === view.penColor);
  };
  for (const color of PEN_COLORS) {
    const swatch = el("button", { type: "button", style: `background:${color}`, "data-color": color, title: color });
    swatch.addEventListener("click", () => {
      view.penColor = color;
      syncSwatches();
    });
    swatches.append(swatch);
  }
  syncSwatches();
  handle.body.append(el("div", { class: "field" }, el("span", { text: t().penColor }), swatches));

  handle.body.append(
    slider(t().penWidth, { min: 1, max: 10, step: 1, value: view.penWidthPx, format: (v) => `${v} px` }, (value) => {
      view.penWidthPx = value;
    }),
  );

  const undoButton = el("button", { type: "button" }, t().undoAnnotation);
  undoButton.addEventListener("click", () => view.undo());
  const clearButton = el("button", { type: "button" }, t().clearAnnotations);
  clearButton.addEventListener("click", () => {
    if (window.confirm(t().confirmClearAnnotations)) view.clearAnnotations();
  });
  handle.body.append(el("div", { class: "row" }, undoButton, clearButton));

  return handle.root;
}
